// The row menu, the space menu and leaving (07-frame.md). Entries the person may not use, or whose
// area isn't built yet (notifications 12, invite 17, settings 18, create 09), are left out.
import { DeleteItem } from '../settings/DeleteRoom';
import type { MatrixClient, Room } from 'matrix-js-sdk';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { Confirm } from '../blocks/Dialog';
import { Menu, MenuItem, MenuLine } from '../blocks/Menu';
import { navigate } from '../core/router';
import { linkTo } from '../core/routes';
import { serverOf } from '../core/session';
import { t } from '../i18n';
import { refFor } from '../session/rooms';
import { frameIcons } from './icons';
import { anyUnread, markAsRead, roomsIn } from './lists';
import { NotificationsItem } from './modes';
import { createChannelIn, mayAddChannels } from './create';
import { inviteTo, mayInvite } from '../people/pages';
import { chatIcons } from '../chat/icons';
import { openRoomSettings } from '../settings/RoomSettings';
import { mayBanner, openBanner } from './Banner';

export interface MenuPlace {
  anchor?: HTMLElement;
  point?: { x: number; y: number };
}

/** Where a menu opens: under its button, or at the pointer on a right-click. */
export function useMenuPlace() {
  const [place, setPlace] = useState<MenuPlace>();
  return {
    place,
    fromButton: (e: MouseEvent<HTMLElement>) => setPlace({ anchor: e.currentTarget }),
    atPointer: (e: MouseEvent) => {
      e.preventDefault();
      setPlace({ point: { x: e.clientX, y: e.clientY } });
    },
    close: () => setPlace(undefined),
  };
}

/** desyr's own link to a room: its main address, else its ID with its server to join through. */
export function copyLink(room: Room): void {
  const ref = refFor(room);
  void navigator.clipboard.writeText(linkTo(ref, undefined, ref.startsWith('!') ? [serverOf(room.roomId)] : []));
}

export function RoomMenu({ client, room, place, onClose, onLeave }: { client: MatrixClient; room: Room; place: MenuPlace; onClose: () => void; onLeave: () => void }) {
  return (
    <Menu label={room.name} {...place} onClose={onClose}>
      <MenuItem icon={frameIcons.doneAll} disabled={!anyUnread([room])} onSelect={() => void markAsRead(client, [room])}>
        {t('frame.markRead')}
      </MenuItem>
      <NotificationsItem client={client} room={room} onDone={onClose} />
      <MenuLine />
      <MenuItem icon={chatIcons.personAdd} disabled={!mayInvite(client, room)} onSelect={() => inviteTo(room)}>
        {t('people.invite')}
      </MenuItem>
      <MenuItem icon={frameIcons.link} onSelect={() => copyLink(room)}>
        {t('frame.copyLink')}
      </MenuItem>
      <MenuItem icon={frameIcons.settings} onSelect={() => openRoomSettings(room)}>
        {t('frame.settings')}
      </MenuItem>
      <MenuLine />
      <MenuItem icon={frameIcons.logout} destructive onSelect={onLeave}>
        {t('frame.leave')}
      </MenuItem>
      <DeleteItem client={client} room={room} />
    </Menu>
  );
}

/** The space menu: the full form above its list, the short form on the rail. */
export function SpaceMenu({ client, space, place, short, onClose, onLeave }: { client: MatrixClient; space: Room; place: MenuPlace; short?: boolean; onClose: () => void; onLeave: () => void }) {
  const rooms = roomsIn(client, space);
  return (
    <Menu label={space.name} {...place} side={short ? 'right' : 'below'} onClose={onClose}>
      <MenuItem icon={frameIcons.doneAll} disabled={!anyUnread(rooms)} onSelect={() => void markAsRead(client, rooms)}>
        {t('frame.markRead')}
      </MenuItem>
      <MenuLine />
      {!short && (
        <MenuItem icon={chatIcons.personAdd} disabled={!mayInvite(client, space)} onSelect={() => inviteTo(space)}>
          {t('people.invite')}
        </MenuItem>
      )}
      <MenuItem icon={frameIcons.link} onSelect={() => copyLink(space)}>
        {t('frame.copyLink')}
      </MenuItem>
      {!short && mayAddChannels(client, space) && (
        <MenuItem icon={frameIcons.add} onSelect={() => createChannelIn(space)}>
          {t('frame.createChannel')}
        </MenuItem>
      )}
      {!short && (
        <MenuItem icon={frameIcons.settings} onSelect={() => openRoomSettings(space)}>
          {t('frame.spaceSettings')}
        </MenuItem>
      )}
      {!short && mayBanner(client, space) && (
        <MenuItem icon={chatIcons.image} onSelect={() => openBanner(space)}>
          {t('frame.changeBanner')}
        </MenuItem>
      )}
      {!short && (
        <MenuItem icon={frameIcons.gridView} onSelect={() => navigate(`/${encodeURIComponent(refFor(space))}/lobby/`)}>
          {t('frame.lobby')}
        </MenuItem>
      )}
      {!short && <MenuLine />}
      {!short && (
        <MenuItem icon={frameIcons.logout} destructive onSelect={onLeave}>
          {t('frame.leaveSpace')}
        </MenuItem>
      )}
      {!short && <DeleteItem client={client} room={space} />}
    </Menu>
  );
}

/** "Leave <name>?"; on success the room goes from every list at once. */
export function LeaveDialog({ client, room, onLeft, onClose }: { client: MatrixClient; room: Room; onLeft: (roomId: string) => void; onClose: () => void }) {
  const space = room.isSpaceRoom();
  const named = room.name && room.name !== room.roomId;
  const title: ReactNode = named ? t('frame.leaveTitle', { name: room.name }) : t(space ? 'frame.leaveSpaceTitle' : 'frame.leaveChannelTitle');
  return (
    <Confirm
      title={String(title)}
      text={t(space ? 'frame.leaveTextSpace' : 'frame.leaveText')}
      action={t('frame.leave')}
      busyText={t('frame.leaving')}
      destructive
      errorPrefix={t('frame.cantLeave')}
      onConfirm={async () => {
        await client.leave(room.roomId);
        onLeft(room.roomId);
      }}
      onClose={onClose}
    />
  );
}

/** A space's main address or ID, as in paths. */
export const spacePath = (space: Room) => `/${encodeURIComponent(refFor(space))}/`;

